import { useEffect, useState } from "react";
import { PHASE_META, ProgressBar } from "@/lib/phases";
import { SetupRoadmap } from "@/components/SetupRoadmap";
import { StepNotes } from "@/components/StepNotes";
import { CheckCircle, Circle, CaretDown, Lightbulb, MapTrifold } from "@phosphor-icons/react";

function SubstepText({ text }) {
  const parts = String(text).split(/(https?:\/\/[^\s]+)/g);
  return (
    <span>
      {parts.map((p, i) =>
        /^https?:\/\//.test(p) ? (
          <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="underline break-all" style={{ color: "var(--bk-accent)" }} data-testid="substep-link">{p}</a>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </span>
  );
}

function StepItem({ step, checked, onToggle, index, stepData, onSaveStepData, entityType }) {
  const [open, setOpen] = useState(false);
  const noteCount = (stepData?.tasks?.length || 0) + (stepData?.note ? 1 : 0);
  return (
    <div className="bk-card p-0 overflow-hidden" data-testid={`step-${step.id}`}>
      <div className="flex items-start gap-4 p-5">
        <button onClick={() => onToggle(!checked)} data-testid={`toggle-${step.id}`} className="mt-0.5 shrink-0" aria-label="Toggle step">
          {checked ? <CheckCircle size={26} weight="fill" color="var(--bk-success)" /> : <Circle size={26} weight="regular" color="var(--bk-disabled)" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <h4 className="font-semibold text-base" style={{ color: checked ? "var(--bk-disabled)" : "var(--bk-text)", textDecoration: checked ? "line-through" : "none" }}>
              <span className="font-mono text-xs mr-2" style={{ color: "var(--bk-accent)" }}>{String(index + 1).padStart(2, "0")}</span>
              {step.title}
            </h4>
            <button onClick={() => setOpen((o) => !o)} className="p-1 rounded hover:bg-[#F5F5F0] flex items-center gap-1.5" data-testid={`detail-${step.id}`}>
              {noteCount > 0 && (
                <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full" style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }} data-testid={`note-count-${step.id}`}>{noteCount}</span>
              )}
              <CaretDown size={18} weight="bold" color="var(--bk-text-2)" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s ease" }} />
            </button>
          </div>
          {open && (
            <div className="mt-3 space-y-3">
              <p className="text-sm leading-relaxed" style={{ color: "var(--bk-text-2)" }}>{step.detail}</p>
              {step.substeps && step.substeps.length > 0 && (
                <ol className="space-y-2" data-testid={`substeps-${step.id}`}>
                  {step.substeps.map((ss, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-relaxed" style={{ color: "var(--bk-text)" }}>
                      <span className="font-mono text-xs mt-0.5 shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }}>{i + 1}</span>
                      <span><SubstepText text={ss} /></span>
                    </li>
                  ))}
                </ol>
              )}
              {step.doc_checklist_by_entity && (() => {
                const docs = step.doc_checklist_by_entity[entityType] || null;
                return (
                  <div className="rounded-md p-3" style={{ background: "var(--bk-success-bg)" }} data-testid={`doc-checklist-${step.id}`}>
                    <p className="text-xs uppercase tracking-[0.12em] font-semibold mb-1.5" style={{ color: "var(--bk-success)" }}>{docs ? `What to bring — ${entityType}` : "What to bring"}</p>
                    {docs ? (
                      <ul className="space-y-1">
                        {docs.map((d, i) => (
                          <li key={i} className="text-sm flex gap-2" style={{ color: "var(--bk-text)" }}><span style={{ color: "var(--bk-success)" }}>•</span>{d}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>Set your entity type in Business → Staff & settings and this checklist will tailor itself to your structure.</p>
                    )}
                  </div>
                );
              })()}
              {step.tip && (
                <div className="flex gap-2 text-sm leading-relaxed rounded-md p-3" style={{ background: "#F6EEDF", color: "var(--bk-text)" }}>
                  <Lightbulb size={18} weight="fill" color="#8A6A3B" className="shrink-0 mt-0.5" />
                  <span><span className="font-semibold">Tip: </span>{step.tip}</span>
                </div>
              )}
              <StepNotes stepId={step.id} stepTitle={step.title} initial={stepData} onSave={onSaveStepData} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function SetupGuides({ business, guides, phases, showRoadmap, initialPhase, isOwnerMode, onToggle, onSaveStepData }) {
  const options = [...phases, ...(showRoadmap ? ["onboarding"] : [])];
  const [phase, setPhase] = useState(options.includes(initialPhase) ? initialPhase : options[0]);
  useEffect(() => { if (initialPhase && options.includes(initialPhase)) setPhase(initialPhase); }, [initialPhase]); // eslint-disable-line react-hooks/exhaustive-deps
  const completed = business.completed_steps || {};

  return (
    <div className="space-y-6" data-testid="setup-guides">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {phases.map((p) => {
          const meta = PHASE_META[p];
          const prog = business.progress?.[p] || { done: 0, total: 0 };
          const active = phase === p;
          return (
            <button key={p} onClick={() => setPhase(p)} data-testid={`phase-card-${p}`} className="bk-card p-5 text-left bk-lift"
              style={{ borderColor: active ? meta.color : "var(--bk-border)", borderWidth: active ? 2 : 1 }}>
              <div className="flex items-center gap-2.5 mb-3">
                <meta.icon size={22} weight="duotone" color={meta.color} />
                <span className="font-display font-bold" style={{ color: "var(--bk-text)" }}>{meta.title}</span>
              </div>
              <ProgressBar done={prog.done} total={prog.total} />
            </button>
          );
        })}
        {showRoadmap && (
          <button onClick={() => setPhase("onboarding")} data-testid="phase-card-onboarding" className="bk-card p-5 text-left bk-lift"
            style={{ borderColor: phase === "onboarding" ? "var(--bk-accent)" : "var(--bk-border)", borderWidth: phase === "onboarding" ? 2 : 1 }}>
            <div className="flex items-center gap-2.5 mb-3">
              <MapTrifold size={22} weight="duotone" color="var(--bk-accent)" />
              <span className="font-display font-bold" style={{ color: "var(--bk-text)" }}>Setup roadmap</span>
            </div>
            <p className="text-xs" style={{ color: "var(--bk-text-2)" }}>Registrations, licenses and accounts for your state.</p>
          </button>
        )}
      </div>

      {phase === "onboarding" ? (
        <SetupRoadmap businessId={business.id} entityType={business.entity_type} />
      ) : (() => {
        const guide = guides[phase];
        if (!guide) return <p style={{ color: "var(--bk-text-2)" }}>Loading guide…</p>;
        const doneSet = new Set(completed[phase] || []);
        let counter = -1;
        return (
          <div className="space-y-8" data-testid={`guide-${phase}`}>
            <p className="text-base max-w-2xl" style={{ color: "var(--bk-text-2)" }}>{guide.subtitle}</p>
            {isOwnerMode && guide.sections.some((s) => s.steps.some((st) => st.mode === "bookkeeper")) && (
              <div className="rounded-xl border px-4 py-3 text-sm" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }} data-testid="owner-mode-note">
                Rowan handles all double-entry mechanics (T-accounts, debits & credits, trial balance) automatically in the background. Want to learn the bookkeeping basics yourself? Switch to Bookkeeper mode in the header to see the optional education steps.
              </div>
            )}
            {guide.sections.map((section) => {
              const visibleSteps = section.steps.filter((st) => !(isOwnerMode && st.mode === "bookkeeper"));
              if (!visibleSteps.length) return null;
              return (
                <div key={section.title}>
                  <h3 className="text-xs uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: "var(--bk-accent)" }}>{section.title}</h3>
                  <div className="space-y-3">
                    {visibleSteps.map((step) => {
                      counter += 1;
                      return (
                        <StepItem key={step.id} step={step} index={counter} checked={doneSet.has(step.id)} onToggle={(v) => onToggle(phase, step.id, v)}
                          stepData={(business.step_data || {})[step.id]} onSaveStepData={onSaveStepData} entityType={business.entity_type || ""} />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}
